/**
 * Agent Tasks Index Page
 *
 * Displays agent tasks in a Kanban board with submitter and search filters.
 * Supports a detail drawer and task creation. Feature-gated by agentTasks flag.
 *
 * @module features/tasks/pages/TasksIndex
 */

import React from 'react';
import {
  DrawerTrigger,
  Button as HiddenButton,
  ToolbarFilterMenu,
  ToolbarFilterMenuTriState,
  type MultiSelectOption,
  type FilterToolbarChip,
} from '@chatsdet/ui';
import { useNavigate } from '@tanstack/react-router';
import { BOARDS } from '@chatsdet/cards-plugins';
import { useAuth } from '../../../contexts/AuthContext';
import { tasksIndexRoute } from '../../../router';
import { KanbanBoard } from '../components/KanbanBoard';
import { TaskDetailDrawer } from '../components/TaskDetailDrawer';
import { CreateTaskDialog } from '../components/CreateTaskDialog';
import { BoardToolbar } from '../../boards/BoardToolbar';
import { PRIORITY_OPTIONS, PRIORITY_LABELS } from '../constants';
import { useTagsQuery } from '../../../hooks/useTags';
import { useCards } from '../../cards/hooks/useCards';
import { useOrgMembers } from '../../../hooks/useOrgMembers';

function getUpdatedAfter(timeframe: string | undefined): string | undefined {
  const now = new Date();
  switch (timeframe ?? '7d') {
    case '24h':
      return new Date(now.getTime() - 24 * 60 * 60 * 1000).toISOString();
    case 'yesterday': {
      const yesterday = new Date(now);
      yesterday.setDate(yesterday.getDate() - 1);
      yesterday.setHours(0, 0, 0, 0);
      return yesterday.toISOString();
    }
    case '7d':
      return new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString();
    case '30d':
      return new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000).toISOString();
    case 'all':
      return undefined;
    default:
      return new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).toISOString();
  }
}

export const TasksIndex: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const search = tasksIndexRoute.useSearch();

  const taskId = search.taskId as string | undefined;
  const timeframe = (search.timeframe as string) || undefined;
  const searchText = search.search ?? '';
  const tagIds = search.tagIds ?? [];
  const excludeTagIds = search.excludeTagIds ?? [];
  const priorityIds = search.priority ?? [];
  const priorities = React.useMemo(
    () => priorityIds.map((p: string) => Number(p)).filter((n: number) => Number.isInteger(n)),
    [priorityIds]
  );
  const customer = (search.customer as string | undefined) || undefined;
  const createdBy = (search.createdBy as string | undefined) || undefined;
  const supportsSearch = BOARDS.agent_task.features.includes('search');

  const membersQuery = useOrgMembers();
  const members = React.useMemo(() => membersQuery.data?.items ?? [], [membersQuery.data]);
  const memberName = React.useCallback(
    (userId: string) => {
      if (userId === user?.id) return 'Me';
      const m = members.find((mm) => mm.userId === userId);
      return m?.displayName || m?.primaryEmail || 'Unknown';
    },
    [members, user?.id]
  );
  const submitterOptions = React.useMemo<MultiSelectOption[]>(() => {
    const me = members.find((m) => m.userId === user?.id);
    const others = members.filter((m) => m.userId !== user?.id);
    return [
      { id: 'agent', label: 'Agent' },
      ...(me ? [{ id: me.userId, label: 'Me' }] : []),
      ...others.map((m) => ({ id: m.userId, label: memberName(m.userId) })),
    ];
  }, [members, user?.id, memberName]);
  const tagsQuery = useTagsQuery();
  const tagOptions = React.useMemo<MultiSelectOption[]>(
    () => tagsQuery.data?.data.map((tag) => ({ id: tag.id, label: tag.name })) ?? [],
    [tagsQuery.data?.data]
  );
  const { data: accounts = [] } = useCards('account');
  const accountOptions = React.useMemo<MultiSelectOption[]>(
    () => accounts.map((account) => ({ id: account.id, label: account.title })),
    [accounts]
  );
  const agentSubmitted: 'all' | 'yes' | 'no' =
    search.agentSubmitted === 'yes' || search.agentSubmitted === 'no'
      ? search.agentSubmitted
      : 'all';

  const completedUpdatedAfter = getUpdatedAfter(timeframe);

  const agentSubmittedParam: 'true' | 'false' | undefined =
    agentSubmitted === 'yes' ? 'true' : agentSubmitted === 'no' ? 'false' : undefined;

  const drawerOpen = !!taskId;

  React.useEffect(() => {
    if (user && !user.features?.agentTasks) {
      void navigate({ to: '/' });
    }
  }, [user, navigate]);

  const handleCardClick = React.useCallback(
    (id: string) => {
      void navigate({
        to: '/boards/tasks',
        search: { ...search, taskId: id },
      });
    },
    [navigate, search]
  );

  const handleDrawerOpenChange = React.useCallback(
    (isOpen: boolean) => {
      if (!isOpen) {
        void navigate({
          to: '/boards/tasks',
          search: { ...search, taskId: undefined },
        });
      }
    },
    [navigate, search]
  );

  const handleSearchChange = React.useCallback(
    (value: string) => {
      void navigate({
        to: '/boards/tasks',
        search: {
          ...search,
          search: value.trim().length > 0 ? value : undefined,
          page: undefined,
        },
      });
    },
    [navigate, search]
  );

  const filterChips = React.useMemo<FilterToolbarChip[]>(() => {
    const chips: FilterToolbarChip[] = [];
    if (agentSubmitted === 'yes') {
      chips.push({ id: 'submitter', label: 'Agent' });
    } else if (createdBy) {
      chips.push({ id: 'submitter', label: memberName(createdBy) });
    }
    for (const id of priorityIds) {
      chips.push({ id: `priority:${id}`, label: PRIORITY_LABELS[Number(id)] ?? `P${id}` });
    }
    for (const id of tagIds) {
      const option = tagOptions.find((tag) => tag.id === id);
      chips.push({ id: `tag:${id}`, label: option?.label ?? 'Tag' });
    }
    for (const id of excludeTagIds) {
      const option = tagOptions.find((tag) => tag.id === id);
      chips.push({ id: `extag:${id}`, label: `−${option?.label ?? 'Tag'}` });
    }
    if (customer) {
      const account = accountOptions.find((a) => a.id === customer);
      chips.push({ id: `customer:${customer}`, label: account?.label ?? 'Customer' });
    }
    return chips;
  }, [
    agentSubmitted,
    createdBy,
    memberName,
    priorityIds,
    tagIds,
    excludeTagIds,
    tagOptions,
    customer,
    accountOptions,
  ]);

  const handleChipRemove = React.useCallback(
    (chipId: string) => {
      if (chipId === 'submitter') {
        void navigate({
          to: '/boards/tasks',
          search: { ...search, agentSubmitted: undefined, createdBy: undefined, page: undefined },
        });
        return;
      }
      if (chipId.startsWith('priority:')) {
        const removed = chipId.slice('priority:'.length);
        const next = priorityIds.filter((id: string) => id !== removed);
        void navigate({
          to: '/boards/tasks',
          search: { ...search, priority: next.length > 0 ? next : undefined, page: undefined },
        });
        return;
      }
      if (chipId.startsWith('tag:')) {
        const removed = chipId.slice('tag:'.length);
        const next = tagIds.filter((id: string) => id !== removed);
        void navigate({
          to: '/boards/tasks',
          search: { ...search, tagIds: next.length > 0 ? next : undefined, page: undefined },
        });
        return;
      }
      if (chipId.startsWith('extag:')) {
        const removed = chipId.slice('extag:'.length);
        const next = excludeTagIds.filter((id: string) => id !== removed);
        void navigate({
          to: '/boards/tasks',
          search: { ...search, excludeTagIds: next.length > 0 ? next : undefined, page: undefined },
        });
        return;
      }
      if (chipId.startsWith('customer:')) {
        void navigate({
          to: '/boards/tasks',
          search: { ...search, customer: undefined, page: undefined },
        });
      }
    },
    [navigate, search, tagIds, excludeTagIds, priorityIds]
  );

  if (!user?.features?.agentTasks) {
    return null;
  }

  return (
    <>
      <div className="flex min-h-0 flex-1 flex-col">
        <BoardToolbar
          searchValue={searchText}
          onSearchChange={supportsSearch ? handleSearchChange : undefined}
          searchPlaceholder="Search tasks"
          filters={
            <>
              <ToolbarFilterMenu
                label="Submitter"
                selectionMode="single"
                options={submitterOptions}
                selectedIds={
                  agentSubmitted === 'yes' ? ['agent'] : createdBy ? [createdBy] : []
                }
                onChange={(ids) => {
                  const id = ids[0];
                  void navigate({
                    to: '/boards/tasks',
                    search: {
                      ...search,
                      agentSubmitted: id === 'agent' ? 'yes' : undefined,
                      createdBy: id && id !== 'agent' ? id : undefined,
                      page: undefined,
                    },
                  });
                }}
              />
              <ToolbarFilterMenu
                label="Priority"
                options={PRIORITY_OPTIONS}
                selectedIds={priorityIds}
                onChange={(ids) => {
                  void navigate({
                    to: '/boards/tasks',
                    search: {
                      ...search,
                      priority: ids.length > 0 ? ids : undefined,
                      page: undefined,
                    },
                  });
                }}
              />
              <ToolbarFilterMenuTriState
                label="Tags"
                options={tagOptions}
                value={{ include: tagIds, exclude: excludeTagIds }}
                isLoading={tagsQuery.isLoading}
                onChange={({ include, exclude }) => {
                  void navigate({
                    to: '/boards/tasks',
                    search: {
                      ...search,
                      tagIds: include.length > 0 ? include : undefined,
                      excludeTagIds: exclude.length > 0 ? exclude : undefined,
                      page: undefined,
                    },
                  });
                }}
              />
              <ToolbarFilterMenu
                label="Customer"
                selectionMode="single"
                options={accountOptions}
                selectedIds={customer ? [customer] : []}
                onChange={(ids) => {
                  void navigate({
                    to: '/boards/tasks',
                    search: { ...search, customer: ids[0], page: undefined },
                  });
                }}
              />
            </>
          }
          actions={<CreateTaskDialog />}
          chips={filterChips}
          onChipRemove={handleChipRemove}
        />

        <KanbanBoard
          onCardClick={handleCardClick}
          completedUpdatedAfter={completedUpdatedAfter}
          search={searchText}
          agentSubmitted={agentSubmittedParam}
          tagIds={tagIds}
          excludeTagIds={excludeTagIds}
          priorities={priorities}
          accountCardId={customer}
          createdBy={createdBy}
          completedTimeframe={timeframe ?? '7d'}
          onCompletedTimeframeChange={(value) => {
            void navigate({
              to: '/boards/tasks',
              search: {
                ...search,
                timeframe: value === '7d' ? undefined : value,
              },
            });
          }}
        />
      </div>

      {/* Detail drawer - shared between both views */}
      <DrawerTrigger isOpen={drawerOpen} onOpenChange={handleDrawerOpenChange}>
        {/* Hidden trigger element - drawer is opened programmatically */}
        <HiddenButton className="hidden">Open</HiddenButton>
        {taskId ? <TaskDetailDrawer taskId={taskId} /> : <div />}
      </DrawerTrigger>
    </>
  );
};
